import { inject, Injectable } from '@angular/core';
import { isAgency, isIndividual, isOffice, isTeam } from '@rma/generic/domain/helpers';
import { FINANCE, ProfileType, SALES, VerticalType } from '@rma/generic/domain/types';
import { objectDistinctUntilChanged, rmaShareReplay } from '@rma/generic/rxjs/operators';
import { nonNullable } from '@rma/generic/rxjs/operators/non-nullable';
import { cache } from 'decorator-cache-getter';
import { combineLatest, distinctUntilChanged, map, Observable } from 'rxjs';
import { ContextSelectorService } from './context-selector.service';
import { CurrentProfileContext, ProfileIdentifier, ProfileIdentifierWithVertical } from './domain/profile-context.models';
import { ProfileContextService } from './profile-context.service';

/**
 * These exist only to keep the deprecated user-type getters typed; prefer profileType/primaryVerticalType.
 */
export type ProfileUserType = 'Agent' | 'Agency';
export type ProfileUserTypeWithVertical = 'Mortgage Broker' | 'Agent' | 'Agency';

/**
 * ProfileContextProjectionService
 *
 * The single home for the derived streams every app reads off the resolved profile context. Apps compose this
 * (the per-app context services delegate to it) and add only their own deltas on top.
 */
@Injectable({
  providedIn: 'root',
})
export class ProfileContextProjectionService {
  private readonly profileContextService = inject(ProfileContextService);
  private readonly profileContextSelector = inject(ContextSelectorService);

  /**
   * Emits a fresh string on every resolution so consumers can pair it with distinctUntilChanged to detect a context change.
   */
  @cache
  public get profileContextChanged$(): Observable<string> {
    return this.profileContextService.currentProfileContext$.pipe(map(() => new Date().toString()));
  }

  @cache
  public get nullableProfileContext$(): Observable<CurrentProfileContext | null> {
    return this.profileContextService.currentProfileContext$.pipe(rmaShareReplay());
  }

  @cache
  public get profileContext$(): Observable<CurrentProfileContext> {
    return this.nullableProfileContext$.pipe(nonNullable());
  }

  @cache
  public get profileCode$(): Observable<string> {
    return this.profileContext$.pipe(map((x) => x.profileCode));
  }

  @cache
  public get profileEmail$(): Observable<string> {
    return this.profileContext$.pipe(map((x) => x.email));
  }

  @cache
  public get profileImage$(): Observable<string> {
    return this.profileContext$.pipe(map((x) => x.profileImage));
  }

  @cache
  public get profileType$(): Observable<ProfileType> {
    return this.profileContext$.pipe(
      map((ctx) => ctx.profileType),
      distinctUntilChanged(),
    );
  }

  @cache
  public get primaryVerticalType$(): Observable<VerticalType> {
    return this.profileContext$.pipe(
      map((ctx) => ctx.primaryVerticalType),
      distinctUntilChanged(),
    );
  }

  @cache
  public get isFinance$(): Observable<boolean> {
    return this.primaryVerticalType$.pipe(map((verticalType) => verticalType === FINANCE));
  }

  @cache
  public get isNotFinance$(): Observable<boolean> {
    return this.primaryVerticalType$.pipe(map((verticalType) => verticalType !== FINANCE));
  }

  @cache
  public get isSales$(): Observable<boolean> {
    return this.primaryVerticalType$.pipe(map((verticalType) => verticalType === SALES));
  }

  @cache
  public get profileIdentifier$(): Observable<ProfileIdentifier> {
    return this.profileContext$.pipe(
      map(({ profileType, profileCode }) => ({ profileType, profileCode })),
      objectDistinctUntilChanged(),
    );
  }

  @cache
  public get profileIdentifierWithVertical$(): Observable<ProfileIdentifierWithVertical> {
    return this.profileContext$.pipe(
      map(({ profileType, profileCode, primaryVerticalType }) => ({
        profileType,
        profileCode,
        verticalType: primaryVerticalType,
      })),
    );
  }

  @cache
  public get nullableProfileIdentifier$(): Observable<ProfileIdentifier | null> {
    return this.nullableProfileContext$.pipe(map((ctx) => (ctx ? { profileType: ctx.profileType, profileCode: ctx.profileCode } : null)));
  }

  /**
   * @deprecated Use profileType$ instead.
   */
  @cache
  public get profileUserType$(): Observable<ProfileUserType> {
    return this.isAgency$.pipe(map((ctx) => (ctx ? 'Agency' : 'Agent')));
  }

  /**
   * @deprecated Use a combination of primaryVerticalType and profileType.
   */
  @cache
  public get profileVerticalUserType$(): Observable<ProfileUserTypeWithVertical> {
    return combineLatest([this.isFinance$, this.profileUserType$]).pipe(
      map(([isFinance, userType]) => (isFinance ? 'Mortgage Broker' : userType)),
    );
  }

  // @deprecated are you sure you don't mean profileIdentifier$ instead?
  @cache
  public get profileUserTypeAndProfileCode$(): Observable<{
    userType: ProfileUserType;
    profileCode: string;
  }> {
    return combineLatest({
      userType: this.profileUserType$,
      profileCode: this.profileCode$,
    }).pipe(rmaShareReplay());
  }

  @cache
  public get isIndividual$(): Observable<boolean> {
    return this.profileType$.pipe(map((profileType) => isIndividual(profileType)));
  }

  @cache
  public get isAgency$(): Observable<boolean> {
    return this.profileType$.pipe(map((profileType) => isAgency(profileType)));
  }

  @cache
  public get isTeam$(): Observable<boolean> {
    return this.profileType$.pipe(map((ctx) => isTeam(ctx)));
  }

  @cache
  public get isOffice$(): Observable<boolean> {
    return this.profileType$.pipe(map((ctx) => isOffice(ctx)));
  }

  @cache
  public get isAgent$(): Observable<boolean> {
    return this.isIndividual$;
  }

  @cache
  public get isMortgageBroker$(): Observable<boolean> {
    return this.isFinance$;
  }

  @cache
  public get profileUrl$(): Observable<string> {
    return this.profileContext$.pipe(map((x) => x.profileUrl));
  }

  @cache
  public get currentAgencyContextCode$(): Observable<string | undefined> {
    return this.profileContext$.pipe(map((x) => (isAgency(x) ? x.profileCode : undefined)));
  }

  @cache
  public get shortName$(): Observable<string> {
    return this.profileContext$.pipe(map(({ shortName }) => shortName));
  }

  @cache
  public get parentName$(): Observable<string> {
    return this.profileContext$.pipe(map(({ parentName }) => parentName ?? ''));
  }

  public refreshProfileContext(): void {
    this.profileContextService.reloadProfileContext();
  }

  public setProfileContext(profileType: ProfileType, profileCode: string): void {
    this.profileContextSelector.setContext({ profileType, profileCode });
  }
}
